import './styles/app.css';
import { MusicEngine } from './audio/MusicEngine';
import {
  fetchNewest,
  fetchTrack,
  trackAudioUrl,
  trackCoverUrl,
  fetchMe,
  type Track,
} from './api/freemusiers';
import { submitScore } from './api/scores';
import { GAME_CATALOG, createGame } from './games/registry';
import type { Game } from './games/types';
import { InputManager, bindPadButton } from './shell/InputManager';
import { TrackPicker } from './shell/TrackPicker';
import { APP_NAME } from './config';

function detectQuality(): 'high' | 'medium' | 'low' {
  const mem = (navigator as Navigator & { deviceMemory?: number }).deviceMemory;
  const cores = navigator.hardwareConcurrency || 4;
  
  if (mem !== undefined && mem <= 2) return 'low';
  if (cores <= 2) return 'medium';
  return 'high';
}

function toast(msg: string, ms = 2200): void {
  const el = document.getElementById('toast');
  if (!el) return;
  el.textContent = msg;
  el.classList.remove('hidden');
  window.setTimeout(() => el.classList.add('hidden'), ms);
}

function boot(): void {
  const app = document.querySelector<HTMLDivElement>('#app');
  if (!app) throw new Error('#app missing');

  const music = new MusicEngine();
  const input = new InputManager();
  
  let quality: 'high' | 'medium' | 'low' =
    (localStorage.getItem('freemusiers_quality') as any) ||
    (localStorage.getItem('bancamp_quality') as any) ||
    detectQuality();

  let currentGame: Game | null = null;
  let currentTrack: Track | null = null;
  let mode: 'home' | 'game' = 'home';
  let isGameOver = false;

  async function handleGameOver(score: number): Promise<void> {
    gameOverOverlay.classList.remove('hidden');
    goScore.textContent = `FINAL SCORE ${score}`;
    goAuth.textContent = 'Checking FreeMusiers ID...';
    
    if (!currentGame || !currentTrack) return;

    try {
      const me = await fetchMe();
      if (me.authenticated && me.user) {
        const name = me.user.display_name || me.user.username;
        goAuth.innerHTML = `
          <div style="display: flex; align-items: center; gap: 8px; justify-content: center; margin-bottom: 12px;">
            ${me.user.avatar ? `<img src="${me.user.avatar}" width="24" height="24" style="border-radius: 50%; object-fit: cover;" />` : ''}
            <span>Saving as <strong>${name}</strong>...</span>
          </div>
        `;
        await submitScore(currentGame.meta.id, currentTrack.id, name, score);
        goAuth.innerHTML += `<div style="color: var(--mint);">Score saved!</div>`;
      } else {
        goAuth.textContent = 'Not logged into FreeMusiers (Score not saved)';
      }
    } catch (err) {
      goAuth.textContent = 'Failed to submit score.';
    }
  }

  let last = performance.now();
  let fpsEma = 60;
  let lowFpsFrames = 0;

  app.innerHTML = `
    <div class="shell">
      <header class="topbar">
        <div class="brand">${APP_NAME}</div>
        <div class="now-playing">
          <img class="now-cover" id="now-cover" alt="" width="56" height="56" />
          <div class="now-text">
            <div class="now-title" id="now-title">No track</div>
            <div class="now-artist" id="now-artist">Pick music to power the games</div>
            <div class="energy-meter" aria-hidden="true"><span id="energy-fill"></span></div>
          </div>
        </div>
        <div class="top-actions">
          <select id="quality-select" class="chip-btn" style="background:transparent; color:inherit; border:1px solid currentColor; cursor:pointer; font-size:10px; padding:0 8px; appearance:none;" title="Quality">
            <option value="high" style="color:#000">HQ</option>
            <option value="medium" style="color:#000">MQ</option>
            <option value="low" style="color:#000">LQ</option>
          </select>
          <button type="button" class="chip-btn" id="btn-music" title="Choose track">MUSIC</button>
          <button type="button" class="icon-btn" id="btn-play" title="Play/Pause" aria-label="Play or pause">▶</button>
          <button type="button" class="icon-btn hidden" id="btn-home" title="Back to games" aria-label="Home">⌂</button>
        </div>
      </header>

      <div class="stage-wrap">
        <div class="stage" id="stage"></div>
        <div class="hud hidden" id="hud">
          <div class="hud-block" id="hud-left">SCORE 0</div>
          <div class="hud-block" id="hud-right">❤ 3 · W1</div>
        </div>
        <div class="hud-msg hidden" id="hud-msg"></div>
        <div class="game-over-overlay hidden" id="game-over">
          <h2>GAME OVER</h2>
          <div class="go-score" id="go-score">SCORE 0</div>
          <div class="go-auth" id="go-auth">Loading profile...</div>
          <button type="button" class="chip-btn" id="btn-go-home" style="margin-top: 1rem;">BACK TO HUB</button>
        </div>
        <section class="home" id="home"></section>
      </div>

      <footer class="console is-home" id="console">
        <div class="pad" id="pad">
          <button type="button" class="pad-btn" data-dir="up" aria-label="Up">▲</button>
          <button type="button" class="pad-btn" data-dir="left" aria-label="Left">◀</button>
          <button type="button" class="pad-btn" data-dir="down" aria-label="Down">▼</button>
          <button type="button" class="pad-btn" data-dir="right" aria-label="Right">▶</button>
        </div>
        <div class="console-hint">CONSOLE<br/>PAD + FIRE<br/><span style="opacity:.7">or keyboard</span></div>
        <div class="fire-wrap">
          <button type="button" class="fire-btn" id="btn-fire" aria-label="Fire">FIRE</button>
        </div>
      </footer>
    </div>
    <div id="toast" class="toast hidden"></div>
  `;

  const stage = document.getElementById('stage')!;
  const home = document.getElementById('home')!;
  const consoleEl = document.getElementById('console')!;
  const hud = document.getElementById('hud')!;
  const hudLeft = document.getElementById('hud-left')!;
  const hudRight = document.getElementById('hud-right')!;
  const hudMsg = document.getElementById('hud-msg')!;
  const gameOverOverlay = document.getElementById('game-over')!;
  const goScore = document.getElementById('go-score')!;
  const goAuth = document.getElementById('go-auth')!;
  const btnGoHome = document.getElementById('btn-go-home')!;
  const nowTitle = document.getElementById('now-title')!;
  const nowArtist = document.getElementById('now-artist')!;
  const nowCover = document.getElementById('now-cover') as HTMLImageElement;
  const energyFill = document.getElementById('energy-fill')!;
  const btnPlay = document.getElementById('btn-play')!;
  const btnHome = document.getElementById('btn-home')!;
  const btnMusic = document.getElementById('btn-music')!;
  const qualitySelect = document.getElementById('quality-select') as HTMLSelectElement;

  qualitySelect.value = quality;
  qualitySelect.addEventListener('change', () => {
    quality = qualitySelect.value as 'high' | 'medium' | 'low';
    localStorage.setItem('freemusiers_quality', quality);
    if (currentGame) {
      const id = currentGame.meta.id;
      void startGame(id);
    }
  });

  const picker = new TrackPicker((t) => {
    void loadTrack(t, true);
  });
  app.appendChild(picker.el);

  bindPadButton(document.querySelector('[data-dir="left"]')!, input, 'left');
  bindPadButton(document.querySelector('[data-dir="right"]')!, input, 'right');
  bindPadButton(document.querySelector('[data-dir="up"]')!, input, 'up');
  bindPadButton(document.querySelector('[data-dir="down"]')!, input, 'down');
  bindPadButton(document.getElementById('btn-fire')!, input, 'fire');

  function renderHome(): void {
    home.innerHTML = `
      <h1>FREEMUSIERS GAMES</h1>
      <p class="lede">
        Music-reactive arcade on the FreeMusiers catalog. Pick a track, then a game —
        difficulty breathes with the mix. Built for phones (console pad) and desktop.
      </p>
      <div class="game-grid">
        ${GAME_CATALOG.map(
          (g) => `
          <button type="button" class="game-card" data-game="${g.id}" ${g.status !== 'playable' ? 'disabled' : ''}>
            <div class="tag">${g.tag}</div>
            <h3>${g.title}</h3>
            <p>${g.blurb}</p>
            ${g.status === 'soon' ? '<span class="badge-soon">SOON</span>' : ''}
          </button>`,
        ).join('')}
      </div>
    `;
    home.querySelectorAll<HTMLButtonElement>('[data-game]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const id = btn.dataset.game!;
        void startGame(id);
      });
    });
  }

  function setNowPlaying(track: Track | null): void {
    if (!track) {
      nowTitle.textContent = 'No track';
      nowArtist.textContent = 'Pick music to power the games';
      nowCover.removeAttribute('src');
      nowCover.classList.add('placeholder');
      return;
    }
    nowTitle.textContent = track.title;
    nowArtist.textContent = track.artist;
    const cover = trackCoverUrl(track.cover_filename);
    if (cover) {
      nowCover.classList.remove('placeholder');
      nowCover.src = cover;
      nowCover.onerror = () => {
        nowCover.removeAttribute('src');
        nowCover.classList.add('placeholder');
      };
    } else {
      nowCover.removeAttribute('src');
      nowCover.classList.add('placeholder');
    }
  }

  async function loadTrack(track: Track, autoplay: boolean): Promise<void> {
    try {
      await music.unlock();
      currentTrack = track;
      setNowPlaying(track);
      await music.load(trackAudioUrl(track));
      if (autoplay) {
        await music.play();
        btnPlay.textContent = '❚❚';
      }
      toast(`${track.title} — ready`);
    } catch (err) {
      console.error(err);
      toast(err instanceof Error ? err.message : 'Track load failed');
    }
  }

  async function ensureMusic(): Promise<boolean> {
    if (currentTrack && music.audio.src) {
      try {
        await music.unlock();
        if (music.audio.paused) await music.play();
        btnPlay.textContent = '❚❚';
        return true;
      } catch {
        toast('Tap play after choosing a track');
        return false;
      }
    }
    try {
      const newest = await fetchNewest(12);
      if (!newest.length) {
        toast('No tracks from API');
        return false;
      }
      await loadTrack(newest[0], true);
      return true;
    } catch (err) {
      toast(err instanceof Error ? err.message : 'API unavailable');
      return false;
    }
  }

  async function startGame(id: string): Promise<void> {
    const game = createGame(id);
    if (!game) {
      toast('Game not available yet');
      return;
    }

    const ok = await ensureMusic();
    if (!ok) {
      picker.open();
      return;
    }

    currentGame?.dispose();
    currentGame = game;
    isGameOver = false;
    gameOverOverlay.classList.add('hidden');
    mode = 'game';
    home.classList.add('hidden');
    consoleEl.classList.remove('is-home');
    hud.classList.remove('hidden');
    btnHome.classList.remove('hidden');

    await game.mount({
      canvas: document.createElement('canvas'),
      mount: stage,
      getBands: () => music.current,
      getWaveform: () => music.waveform,
      isMusicPlaying: () => music.isPlaying,
      quality,
    });

    const rect = stage.getBoundingClientRect();
    game.resize(rect.width, rect.height);
    toast(`${game.meta.title} — go`);
  }

  function goHome(): void {
    currentGame?.dispose();
    currentGame = null;
    mode = 'home';
    stage.innerHTML = '';
    home.classList.remove('hidden');
    consoleEl.classList.add('is-home');
    hud.classList.add('hidden');
    hudMsg.classList.add('hidden');
    btnHome.classList.add('hidden');
    gameOverOverlay.classList.add('hidden');
  }

  btnMusic.addEventListener('click', () => picker.open());
  btnHome.addEventListener('click', () => goHome());
  btnGoHome.addEventListener('click', () => goHome());
  btnPlay.addEventListener('click', async () => {
    try {
      await music.unlock();
      if (!music.audio.src) {
        picker.open();
        return;
      }
      if (music.audio.paused) {
        await music.play();
        btnPlay.textContent = '❚❚';
      } else {
        music.pause();
        btnPlay.textContent = '▶';
      }
    } catch {
      toast('Playback blocked — tap again');
    }
  });

  music.audio.addEventListener('ended', () => {
    btnPlay.textContent = '▶';
  });

  const ro = new ResizeObserver(() => {
    if (!currentGame) return;
    const rect = stage.getBoundingClientRect();
    currentGame.resize(rect.width, rect.height);
  });
  ro.observe(stage);

  function frame(now: number): void {
    requestAnimationFrame(frame);
    const dt = Math.min(0.05, (now - last) / 1000);
    last = now;

    const bands = music.tick();
    energyFill.style.width = `${Math.round(bands.energy * 100)}%`;

    // soft adaptive quality signal (future games can read)
    const fps = dt > 0 ? 1 / dt : 60;
    fpsEma = fpsEma * 0.9 + fps * 0.1;
    if (fpsEma < 28) lowFpsFrames++;
    else lowFpsFrames = Math.max(0, lowFpsFrames - 1);

    if (mode !== 'game' || !currentGame) return;

    const state = input.sample();
    if (!isGameOver) {
      currentGame.update(dt, state);
    }
    
    const h = currentGame.getHud();
    hudLeft.textContent = `SCORE ${h.score}`;
    hudRight.textContent = `❤ ${h.lives} · W${h.wave}`;
    
    if (h.lives <= 0 && !isGameOver) {
      isGameOver = true;
      hudMsg.classList.add('hidden');
      void handleGameOver(h.score);
    } else if (!isGameOver && h.message) {
      hudMsg.textContent = h.message;
      hudMsg.classList.remove('hidden');
    } else if (!isGameOver) {
      hudMsg.classList.add('hidden');
    }
  }

  renderHome();
  requestAnimationFrame(frame);

  // Deep link ?track=&game=
  const params = new URLSearchParams(location.search);
  const trackId = params.get('track');
  const gameId = params.get('game');
  if (trackId) {
    void (async () => {
      try {
        const t = await fetchTrack(trackId);
        await loadTrack(t, false);
        if (gameId) void startGame(gameId);
      } catch {
        toast('Deep-link track failed');
      }
    })();
  }

  // Expose for debug
  const debugHandle = {
    music,
    quality,
    get fps() {
      return fpsEma;
    },
    get lowFpsFrames() {
      return lowFpsFrames;
    },
  };
  (window as unknown as { __freemusiersGames: unknown }).__freemusiersGames = debugHandle;
  (window as unknown as { __bancampGames: unknown }).__bancampGames = debugHandle;
}

boot();
